import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type PortalAlertHandler = (message: string) => void;

interface PortalAlertContextType {
  alertDialogOpen: boolean;
  alertDialogMessage: string;
  showAlert: PortalAlertHandler;
  closeAlert: () => void;
}

const defaultShowAlert: PortalAlertHandler = (message) => {
  window.alert(message);
};

const PortalAlertContext = createContext<PortalAlertContextType>({
  alertDialogOpen: false,
  alertDialogMessage: '',
  showAlert: defaultShowAlert,
  closeAlert: () => undefined,
});

interface PortalAlertProviderProps {
  children: React.ReactNode;
}

export const PortalAlertProvider = ({ children }: PortalAlertProviderProps) => {
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertDialogMessage, setAlertDialogMessage] = useState('');

  const showAlert = useCallback((message: string) => {
    setAlertDialogMessage(message);
    setAlertDialogOpen(true);
  }, []);

  const closeAlert = useCallback(() => {
    setAlertDialogOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      alertDialogOpen,
      alertDialogMessage,
      showAlert,
      closeAlert,
    }),
    [alertDialogOpen, alertDialogMessage, showAlert, closeAlert]
  );

  return (
    <PortalAlertContext.Provider value={value}>
      {children}
    </PortalAlertContext.Provider>
  );
};

export const usePortalAlert = () => useContext(PortalAlertContext).showAlert;

export const usePortalAlertDialog = () => {
  const { alertDialogOpen, alertDialogMessage, closeAlert } =
    useContext(PortalAlertContext);

  return {
    alertDialogOpen,
    alertDialogMessage,
    closeAlert,
  };
};
